import React, { useState, useEffect, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "chart.js/auto";
import "./Consumption.css";
import Papa from "papaparse";
import { useSimulationData } from "../../../../hooks/useSimulationData";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { readColumn } from "../../utils/columnLookup";
import { calculateConsumptionTotals } from "../../utils/consumptionTotals";
import { CONSUMPTION_LINE_COLOR } from "../../utils/deviceColors";
import { parseSimulationDate } from "../../utils/simulationDate";
import {
  buildDataZoom,
  darkTooltip,
  formatDate,
  getXAxisTitle,
} from "../../utils/chartOptions";

/* The line's translucent fill and zoom filler, derived from the one colour
 * rather than restated as a second literal - they were `rgba(255, 30, 30, ...)`
 * beside a `rgba(255, 30, 30, 1)` stroke, three copies of one hex. */
const withAlpha = (hex, alpha) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const CONSUMPTION_FILL_COLOR = withAlpha(CONSUMPTION_LINE_COLOR, 0.15);

export default function ConsumptionChart({ csvData, onLoadingStateChange }) {
  const { dailyData, allTimeData, isLoading, loadingMessage, error, refetchData } =
    useSimulationData(csvData, onLoadingStateChange);

  

  const formatSelectedPeriod = () => {
    if (view === "daily") {
      const [year, month, day] = selectedDate.split("-");
      return `${month}/${day}/${year}`; // MM/DD/YYYY
    } else if (view === "monthly") {
      const [year, month] = selectedMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${year}`;
    } else if (view === "yearly") {
      return selectedYear;
    } else if (view === "all time view") {
      return "";
    }
    return "";
  };

  const formatSelectedPeriodType = () => {
    if (view === "daily") {
      return "Day";
    } else if (view === "monthly") {
      return "Month";
    } else if (view === "yearly") {
      return "Year";
    } else if (view === "all time view") {
      return "All Time View";
    }
    return "";
  };

  const { setExportedData } = useExportedData();
  const [view, setView] = useState("yearly");
  const [chartData, setChartData] = useState([]);

  // Add missing state variables for simulation tracking
  const [currentSimulationTimeKey, setCurrentSimulationTimeKey] =
    useState(null);
  const [maxKnownCreationDate, setMaxKnownCreationDate] = useState(null);

  /* The Date column reaches here as whatever string the result file carried —
   * "01/01/2040 00:00" from the sim, ISO from other writers — so every period
   * key below comes from a parse rather than a fixed offset into the stamp.
   * "01/01/2040".slice(0, 4) is "01/0", which matches Jan 1-9 of any year: a
   * yearly view built that way both truncates and cross-contaminates years.
   * One parse per row, positionally aligned with dailyData. */
  const periodKeys = useMemo(
    () => dailyData.map((row) => parseSimulationDate(row?.Date)),
    [dailyData],
  );

  /* First and last stamp the result actually covers. The native pickers below
   * reject a min/max they cannot read as ISO, which these keys always are. */
  const [firstKey, lastKey] = useMemo(() => {
    const readable = periodKeys.filter(Boolean);
    return [readable[0] ?? null, readable[readable.length - 1] ?? null];
  }, [periodKeys]);

  const availableYears = useMemo(
    () => Array.from(new Set(periodKeys.filter(Boolean).map((key) => key.year))),
    [periodKeys],
  );

  // Date state variables
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedyearRange, setSelectedYearRange] = useState("");

  useEffect(() => {
    const storedKey = sessionStorage.getItem("currentSimulationTimeKey");
    const storedMaxDate = sessionStorage.getItem("maxKnownCreationDate");

    if (storedKey) {
      setCurrentSimulationTimeKey(storedKey);
    }
    if (storedMaxDate) {
      setMaxKnownCreationDate(storedMaxDate);
    }
  }, []);

  useEffect(() => {
    if (!firstKey) return;
    setSelectedDate(firstKey.day);
    setSelectedMonth(firstKey.month);
    setSelectedYear(firstKey.year);
    const years = availableYears.map(Number);
    if (!years.length) return;
    const minYear = years.reduce((a, b) => Math.min(a, b));
    const maxYear = years.reduce((a, b) => Math.max(a, b));
    setSelectedYearRange(`${minYear} - ${maxYear}`);
  }, [firstKey, availableYears]);

  /* Falls back to the first period the result covers rather than slicing on an
   * empty selector. The selectors are filled by an effect, so for one render
   * after a view switch the new view's selector is still "". The offset-slicing
   * version this replaced papered over that case only for the daily view, and
   * only by accident: `Date.startsWith("")` is true of every row, so switching
   * to Daily briefly plotted the WHOLE dataset under a one-day label, while
   * switching to Monthly or Yearly plotted nothing. Slicing on the first period
   * instead shows the right rows immediately in all three, and keeps the chart
   * mounted - an empty chartData unmounts ReactECharts, which disposes the
   * instance and takes the user's zoom range and legend selection with it. */
  const activeDay = selectedDate || firstKey?.day || "";
  const activeMonth = selectedMonth || firstKey?.month || "";
  const activeYear = selectedYear || firstKey?.year || "";

  // This useEffect filters dailyData based on the view and the corresponding selected date/month/year.
  useEffect(() => {
    if (view === "daily") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.day === activeDay),
      );
    } else if (view === "monthly") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.month === activeMonth),
      );
    } else if (view === "yearly") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.year === activeYear),
      );
    } else if (view === "all time view") {
      setChartData(dailyData);
    }
  }, [
    periodKeys,
    view,
    activeDay,
    activeMonth,
    activeYear,
    selectedyearRange,
    dailyData,
  ]);

  /* One rule for the Consumption card, shared with Summary/OverlayChart.
   *
   * `setExportedData` is a full REPLACE and both components write this same
   * app-level slot, so anything this payload omits is anything the Summary card
   * LOSES the moment a reader opens the Consumption tab. It omitted `loadCost`,
   * which is the Load row's money -- so on the page that owns this card the row
   * was always an em-dash carrying "this result file predates the column.
   * Re-run to populate it", advice that is false: `Building Load Load Cost ($)`
   * is in every result header, including husky-2040-prod's.
   *
   * The local `calculateTotalAreaAndCost` that used to sit here is gone with
   * it. It had drifted from OverlayChart's same-named function -- it always
   * summed the load column while OverlayChart preferred the engine's own
   * `Totals Consumption Cost ($)` -- so the card's total changed value
   * depending on which tab rendered last. utils/consumptionTotals.js is now the
   * only definition. */
  useEffect(() => {
    const { totalArea, totalCost, maxPower, loadCost } =
      calculateConsumptionTotals(chartData);
    setExportedData({
      totalAreaUnderCurve: totalArea,
      totalCost,
      maxPower,
      loadCost,
      view: formatSelectedPeriodType(),
      selectedPeriod: formatSelectedPeriod(),
      /* The period as ISO KEYS, so the card can slice the daily summary for its
       * demand row with the same predicate every other surface uses rather than
       * reverse-parsing "January 2040" out of the label above. `periodView` is
       * the raw view token, not the "Day"/"Month"/"Year" display string.
       *
       * From the ACTIVE keys rather than the selector state: the selectors are
       * filled by an effect, so for one render after a view switch the new
       * view's selector is still "" while the totals beside these were already
       * computed from the fallback. */
      periodView: view,
      selDate: activeDay,
      selMonth: activeMonth,
      selYear: activeYear,
    });
  }, [
    chartData,
    view,
    selectedDate,
    selectedMonth,
    selectedYear,
    activeDay,
    activeMonth,
    activeYear,
  ]);

  // Handle changes in the date/month/year input
  const handleDateChange = (e) => {
    if (view === "daily") {
      setSelectedDate(e.target.value);
    } else if (view === "monthly") {
      setSelectedMonth(e.target.value);
    } else if (view === "yearly") {
      setSelectedYear(e.target.value);
    } else if (view === "all time view") {
      setSelectedYearRange(e.target.value);
    }
  };

  useEffect(() => {
    if (onLoadingStateChange) {
      const loadingState = isLoading || dailyData.length === 0;
      
      onLoadingStateChange(loadingState);
    }
  }, [isLoading, dailyData.length, onLoadingStateChange]);

 

  // Skeleton Components
  const ControlsSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="controls-production" style={{ marginBottom: "20px" }}>
        <Skeleton width={120} height={35} style={{ marginRight: "10px" }} />
        <Skeleton width={150} height={35} />
      </div>
    </SkeletonTheme>
  );

  const ChartSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="chart-wrapper">
        <Skeleton height={400} style={{ marginBottom: "20px" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Skeleton width={100} height={20} />
          <Skeleton width={200} height={20} />
          <Skeleton width={100} height={20} />
        </div>
      </div>
    </SkeletonTheme>
  );

  const LoadingStateContent = () => (
    <div className="chart-container-consumption">
      <h3>Consumption History</h3>
      <ControlsSkeleton />
      <ChartSkeleton />
    </div>
  );

  // Show loading state if data is not ready
  if (error && !isLoading) {
    return (
      <div className="chart-container-consumption">
        <h3>Consumption History</h3>
        <div
          style={{
            border: "1px solid #fca5a5",
            background: "#fff1f2",
            color: "#991b1b",
            borderRadius: 10,
            padding: 12,
            marginTop: 12,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Failed to load consumption timeseries
          </div>
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{String(error)}</div>
        </div>
        <button
          type="button"
          className="add-device-save-button"
          style={{ marginTop: 12 }}
          onClick={() => {
            // useSimulationData exposes refetchData via context
            if (typeof refetchData === "function") refetchData();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || dailyData.length === 0) {
    return <LoadingStateContent />;
  }

  return (
    <div className="chart-container-consumption">
      <h3>Consumption History</h3>

      {dailyData.length > 0 && (
        <div className="controls-transfer">
          <select
            onChange={(e) => setView(e.target.value)}
            value={view}
            style={{ marginRight: "10px", cursor: "pointer" }}
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="all time view">All Time View</option>
          </select>

          {view === "daily" && (
            <input
              type="date"
              onChange={handleDateChange}
              value={selectedDate}
              min={firstKey?.day || ""}
              max={lastKey?.day || ""}
              style={{ marginRight: "10px" }}
            />
          )}

          {view === "monthly" && (
            <input
              type="month"
              onChange={handleDateChange}
              value={selectedMonth}
              min={firstKey?.month || ""}
              max={lastKey?.month || ""}
              style={{ marginRight: "10px" }}
            />
          )}

          {view === "yearly" && (
            <select
              onChange={handleDateChange}
              value={selectedYear}
              className="view-dropdown"
              style={{ cursor: "pointer" }}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <div className="chart-wrapper" style={{ height: 600 }}>
        {chartData.length > 0 &&
        chartData.some(
          (entry) =>
            entry["Load (kWh)"] !== undefined ||
            entry["Building Load Load (kWh)"] !== undefined,
        ) ? (
          <ReactECharts
            option={{
              // Animation configuration
              animation: true,
              animationDuration: 1000,
              animationEasing: "cubicInOut",
              animationThreshold: 2000,

              // Progressive rendering
              progressive: 500,
              progressiveThreshold: 3000,

              // Loading state
              loading: {
                text: "Loading...",
                color: "#675AFF",
                textColor: "#675AFF",
                maskColor: "rgba(255, 255, 255, 0.8)",
                zlevel: 0,
              },

              tooltip: darkTooltip({ fontSize: 12, borderColor: "white" }),

              /* The Consumption line's colour, imported rather than typed.
               * deviceColors.js measures every device colour against this hex;
               * the literal that used to be here was the same colour in a
               * notation that module's sweep cannot parse. */
              dataZoom: buildDataZoom({
                accent: CONSUMPTION_LINE_COLOR,
                fillerColor: CONSUMPTION_FILL_COLOR,
              }),

              grid: {
                left: "40px",
                right: "10px",
                top: "20px",
                bottom: "80px",
                containLabel: true,
              },
              xAxis: {
                type: "category",
                
                data:
                  view === "all time view"
                    ? allTimeData.map((entry) => formatDate(entry.Date))
                    : chartData.map((entry) => formatDate(entry.Date)),
                name: getXAxisTitle(view),
                nameLocation: "middle",
                nameGap: 30,
              },
              yAxis: {
                type: "value",
                name: "Energy (kWh)",
                nameLocation: "middle",
                nameGap: 40,
              },
              series: [
                {
                  data:
                    view === "all time view"
                      ? allTimeData.map((entry) => {
                          const v = parseFloat(
                            readColumn(entry, [
                              "Load (kWh)",
                              "Building Load Load (kWh)",
                            ]),
                          );
                          return -Math.abs(v || 0);
                        })
                      : chartData.map((entry) => {
                          const v = parseFloat(
                            readColumn(entry, [
                              "Load (kWh)",
                              "Building Load Load (kWh)",
                            ]),
                          );
                          return -Math.abs(v || 0);
                        }),
                  type: "line",
                  smooth: true,
                  symbol: "circle",
                  symbolSize: 2,
                  lineStyle: {
                    width: 2,
                    color: CONSUMPTION_LINE_COLOR,
                  },
                  itemStyle: {
                    color: CONSUMPTION_LINE_COLOR,
                  },
                  areaStyle: {
                    color: CONSUMPTION_FILL_COLOR,
                  },
                },
              ],
            }}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "svg" }}
          />
        ) : (
          <p className="no-results-message" style={{ textAlign: "center" }}>
            No results are currently available for the selected period.
          </p>
        )}
      </div>
    </div>
  );
}
